/**
 * EmergencyMenu — GLOBAL snippet (iter 241).
 *
 * Replaces the red top strip. Sits INSIDE `<PlatformHeader />` as a
 * single nav item that opens a dropdown containing:
 *   • Police 000     (tap → tel:000)
 *   • Ambulance 000  (tap → tel:000)
 *   • Fire 000       (tap → tel:000)
 *   • Nearest emergency  (opens the nearest-hospital list, reuses
 *     `/api/directory/restaurants?niche=Medical&sub_niche=Hospital`)
 *
 * Snippet-first: DO NOT create per-module emergency variants. Extend
 * this file if you need a different service list.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Ambulance, Flame, Hospital, ChevronDown, Phone } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

const SERVICES = [
  { id: "police",    label: "Police",    number: "000", Icon: Shield },
  { id: "ambulance", label: "Ambulance", number: "000", Icon: Ambulance },
  { id: "fire",      label: "Fire",      number: "000", Icon: Flame },
];

export default function EmergencyMenu({ userLoc, testid = "emergency-menu", className = "" }) {
  const [open, setOpen] = useState(false);
  const [hospitals, setHospitals] = useState([]);
  const [loadingHosp, setLoadingHosp] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDown(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    function onKey(e)  { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    if (!open || hospitals.length > 0) return;
    const lat = userLoc?.lat ?? -27.4698;   // Brisbane CBD default
    const lng = userLoc?.lng ?? 153.0251;
    setLoadingHosp(true);
    fetch(`${API}/api/directory/restaurants?niche=Medical&sub_niche=Hospital&user_lat=${lat}&user_lng=${lng}&sort=distance&limit=5`)
      .then((r) => r.json())
      .then((d) => setHospitals(d.results || []))
      .catch(() => setHospitals([]))
      .finally(() => setLoadingHosp(false));
  }, [open, hospitals.length, userLoc]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid={`${testid}-trigger`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        className={"inline-flex items-center gap-1.5 text-sm tracking-wide transition-colors " +
                   "text-red-700 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 " +
                   className}
      >
        <Phone className={`w-3.5 h-3.5 ${open ? "" : "animate-pulse"}`} strokeWidth={2.5} />
        <span>Emergency</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2} />
      </button>
      {open && (
        <div
          data-testid={`${testid}-panel`}
          className="absolute right-0 top-full mt-2 w-80 z-50
                     rounded-xl border border-stone-200 dark:border-stone-700
                     bg-white dark:bg-stone-900 shadow-2xl overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-stone-200 dark:border-stone-700 bg-red-50 dark:bg-red-950/40">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-red-700 dark:text-red-300">
              In case of emergency
            </p>
            <p className="text-[11px] text-stone-600 dark:text-stone-400 mt-0.5">
              Call 000 for Police, Ambulance or Fire — one national number, 24/7.
            </p>
          </div>
          <div className="p-2 flex flex-col gap-1">
            {SERVICES.map(({ id, label, number, Icon }) => (
              <a
                key={id}
                href={`tel:${number}`}
                data-testid={`${testid}-${id}`}
                className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg
                           hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
              >
                <span className="flex items-center gap-2.5 text-[13px] font-semibold text-stone-800 dark:text-stone-100">
                  <Icon className="w-4 h-4 text-red-700 dark:text-red-400" strokeWidth={2} />
                  {label}
                </span>
                <span className="font-mono text-[13px] font-bold text-red-700 dark:text-red-300">
                  {number}
                </span>
              </a>
            ))}
          </div>
          <div className="border-t border-stone-200 dark:border-stone-700 p-2">
            <div className="px-3 pt-1 pb-1.5 flex items-center gap-1.5">
              <Hospital className="w-3.5 h-3.5 text-stone-600 dark:text-stone-400" />
              <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-stone-600 dark:text-stone-400">
                Nearest hospitals
              </p>
            </div>
            {loadingHosp && (
              <div className="px-3 py-2 text-[12px] text-stone-500 dark:text-stone-400">Locating…</div>
            )}
            {!loadingHosp && hospitals.length === 0 && (
              <div className="px-3 py-2 text-[12px] text-stone-500 dark:text-stone-400">
                No hospitals near your location.
              </div>
            )}
            {hospitals.map((h) => (
              <Link
                key={h.slug || h.id}
                to={`/business/${h.slug}`}
                data-testid={`${testid}-hospital-${h.slug || h.id}`}
                className="block px-3 py-2 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
                onClick={() => setOpen(false)}
              >
                <p className="text-[12px] font-semibold text-stone-900 dark:text-stone-100 truncate">
                  {h.name}
                </p>
                <p className="text-[11px] text-stone-500 dark:text-stone-400 truncate">
                  {[h.address, h.suburb, h.postcode].filter(Boolean).join(", ")
                    || h.district
                    || "Address unavailable"}
                  {h.distance_km ? ` · ${h.distance_km.toFixed(1)} km` : ""}
                </p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
